import React from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Info, X } from 'lucide-react';

/**
 * UnifiedAlert — unified banner for Success / Warning / Error / Info.
 *
 * Always uses semantic tokens so colors stay consistent across pages.
 *
 * Props:
 *  - variant: 'success' | 'warning' | 'error' | 'info'
 *  - title?: string
 *  - children: body content
 *  - onClose?: function — adds dismiss button
 *  - icon: optional custom icon override
 */
export const UnifiedAlert = ({
  variant = 'info',
  title,
  children,
  onClose,
  icon,
  className = '',
  testId,
}) => {
  const map = {
    success: {
      bg: 'var(--mesp-success-soft)',
      border: 'var(--mesp-success-border)',
      color: 'var(--mesp-success)',
      Icon: CheckCircle2,
    },
    warning: {
      bg: 'var(--mesp-warning-soft)',
      border: 'var(--mesp-warning-border)',
      color: 'var(--mesp-warning)',
      Icon: AlertTriangle,
    },
    error: {
      bg: 'var(--mesp-danger-soft)',
      border: 'var(--mesp-danger-border)',
      color: 'var(--mesp-danger)',
      Icon: XCircle,
    },
    info: {
      bg: 'var(--mesp-info-soft)',
      border: 'var(--mesp-info-border)',
      color: 'var(--mesp-info)',
      Icon: Info,
    },
  }[variant];

  const Icon = icon || map.Icon;

  return (
    <div
      role="alert"
      data-testid={testId}
      className={`mesp-ui ${className}`}
      style={{
        background: map.bg,
        border: `1px solid ${map.border}`,
        borderRadius: 'var(--mesp-radius-md)',
        padding: 'var(--mesp-space-4)',
        display: 'flex',
        gap: 'var(--mesp-space-3)',
        alignItems: 'flex-start',
        color: 'var(--mesp-text)',
      }}
    >
      <Icon size={20} style={{ color: map.color, flexShrink: 0, marginTop: 2 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        {title && (
          <div
            style={{
              fontSize: 'var(--mesp-font-body)',
              fontWeight: 'var(--mesp-font-weight-semibold)',
              color: 'var(--mesp-text)',
              marginBottom: children ? 'var(--mesp-space-1)' : 0,
            }}
          >
            {title}
          </div>
        )}
        {children && (
          <div style={{ fontSize: 'var(--mesp-font-sm)', color: 'var(--mesp-text-muted)' }}>
            {children}
          </div>
        )}
      </div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="إغلاق"
          data-testid={testId ? `${testId}-close` : undefined}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--mesp-text-muted)',
            padding: 4,
            borderRadius: 'var(--mesp-radius-sm)',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
};

export default UnifiedAlert;
